import { JUTRO_TOPICS, subscribe } from '@jutro/events';
import _ from 'lodash';
import {
    SgSubmissionUtil, ADOBE_TOPICS, SgSessionStorageService
} from 'saga-modules';
import { SG_AA_PAGE_TYPE, SG_AA_PREMIUM_PAGE_ACTION_PAGES } from 'sg-adobe-analytics/AdobeAnalyticsConstants';
import CommonConstants from 'sg-common-constants';
import CommonAdobeAnalyticsUtil from './CommonAdobeAnalyticsUtil';

let { tagging } = {};
let { SG_AA_FIELDS } = {};
let isQuoteAndBuy;
let eventsSubscribed = false;

let savedPageName;
let fieldErrorSet;

function setAdobeTaggingProp(key, value) {
    if (_.isUndefined(tagging[key]) && _.isUndefined(value)) { // Do not set undefined values
        return;
    }
    if (_.isBoolean(value)) { // Convert boolean values to Yes/No String
        tagging[key] = value === true ? 'yes' : 'no';
    } else if (_.isUndefined(value)) { // If setting a previously existing field to undefined, clear it
        delete (tagging[key]);
    } else {
        tagging[key] = value;
    }
}

/**
 * Appends a new validation to the validation string field
 * @param {event} event
 */
function trackFieldError(event) {
    const { adobeID, value } = event;
    if (!fieldErrorSet) {
        fieldErrorSet = new Set();
    }
    _.each(value, (error) => {
        fieldErrorSet.add(error);
    });
    tagging[adobeID] = Array.from(fieldErrorSet);
}

/**
 * Replaces the validation field with an empty string
 */
function clearFieldError() {
    fieldErrorSet = undefined;
    delete tagging.fieldErrorArray;
}

/**
 * Performs any necessary cleanup in the tracking object to avoid send one-time data twice.
 */
function cleanup() {
    delete tagging.addedProductsArray;
    delete tagging.removedProductsArray;
}

/**
 * Removes all properties from the tagging object.
 */
function reset() {
    if (!tagging.clickThroughId) {
        _.each(SG_AA_FIELDS, (property) => {
            delete tagging[property];
        });
    }
}

/**
 * Sets a new value in an incremental, pipe-delimited field.
 * @param {Object} key Property key
 * @param {*} value value to add
 */

function setHistoryField(key, value) {
    if (tagging[key.id]) { // If already exists, add pipe delimiter
        if (!tagging[key.id].includes(value)) {
            tagging[key.id] += '|';
        }
    } else { // If not, initialise it
        tagging[key.id] = '';
    }
    if (!tagging[key.id].includes(value)) {
        tagging[key.id] += value; // Append the value
    }
}

/**
 * Sets Adobe field and calls Adobe.
 * @param {Object} event Payload from GW Event object.
 */
function setAndTrackAdobeField(event) {
    const { model } = event;
    let { value, adobeID } = event;
    if (_.isNil(adobeID)) {
        return;
    }
    if (_.isNil(adobeID.id)) {
        adobeID = SG_AA_FIELDS[adobeID];
    }
    if (!event.isAddOnChange) {
        cleanup();
    }
    // Set value in Adobe tagging object
    if (adobeID) {
        if (!_.isNil(value)) {
            if (adobeID.dependent) {
                _.each(adobeID.dependent, (dependentField) => {
                    // Generate dependent field's value from source
                    const dependentFieldData = SG_AA_FIELDS[dependentField];
                    const dependentValue = dependentFieldData.getValue(value, model);
                    setAdobeTaggingProp(dependentFieldData.id, dependentValue);
                });
            }
            if (adobeID.transform) { // Run the transformation function if set
                value = adobeID.transform(value);
            }
        }
        setAdobeTaggingProp(adobeID.id, value);
        if (event.trackLastInteraction === true) { // Record last interaction
            tagging.lastInteraction = adobeID.id;
        }
    }
}

/**
 * Tracks an add-on coverage selection change.
 * @param {Object} event GW Event object
 */
function trackAddonChange(event) {
    cleanup();
    if (event.model.removedProductsArray || event.addonsToDelete) { // Remove AddOns
        const addonsToRemove = event.model.removedProductsArray ? event.model.removedProductsArray : event.addonsToDelete;
        const newAddOnsArray = [];
        _.each(tagging.addOnsArray, (addOn) => { // Loop through existing addOns
            if (addOn.addOnProduct.includes(addonsToRemove[0].productRemoved)) {
                addonsToRemove[0].productRemoved = addOn.addOnProduct;
            }
            const removedProductIndex = _.findIndex(addonsToRemove,
                { productRemoved: addOn.addOnProduct });
            if (removedProductIndex === -1) { // If not found in the removedProductsArray, keep it
                newAddOnsArray.push(addOn);
            }
            if (_.findIndex(tagging.addOnsArray,
                { addOnProduct: addonsToRemove[0].productRemoved }) !== -1) {
                tagging.removedProductsArray = event.model.removedProductsArray;
            }
        });
        tagging.addOnsArray = newAddOnsArray; // Re-assign to new array
    }
    if (!_.isEmpty(event.addOnsArray)) { // Set new Addons
        delete tagging.removedProductsArray;
        if (tagging.addOnsArray) {
            event.addOnsArray.forEach((addon) => {
                // only add an addon if it is not already in the addOnsArray
                if (tagging.addOnsArray.findIndex((taggingAddon) => taggingAddon.addOnProduct === addon.addOnProduct) === -1) {
                    tagging.addOnsArray = tagging.addOnsArray.concat([addon]);
                }
                if (addon.value) {
                    if (tagging.addOnsArray.findIndex((taggingAddon) => (taggingAddon.value === addon.value)) === -1) {
                        const index = tagging.addOnsArray.findIndex((taggingAddon) => taggingAddon.addOnProduct === addon.addOnProduct);
                        tagging.addOnsArray[index].value = addon.value;
                    }
                }
            });
            delete tagging.removedProductsArray;
        } else {
            tagging.addOnsArray = event.addOnsArray;
        }
        tagging.addedProductsArray = event.model.addedProductsArray;
    }
    if (tagging.addOnsArray && (tagging.addOnsArray.length === 0)) { // Clear addOnsArray if empty
        delete tagging.addedProductsArray;
        delete tagging.addOnsArray;
    }
}

/**
 * Tracks a pageName change event.
 * @param {Object} event GW Event object
 */
function trackPageName(event) {
    const { page } = event;
    const pageName = CommonAdobeAnalyticsUtil.constructPageModel(page);
    tagging.pageName = pageName;
    /** Saves pageName tagging for use to track previousPageName**/
    savedPageName = page;
    clearFieldError();
}

/**
 * Tracks a previousPageName change event.
 * @param {Object} event GW Event object
 */
function trackPreviousPageName() {
    const pagesNotToTrack = ['quoteentry', 'home', 'renewalentry', 'sg-spm-quoteentry', 'ipid', 'journey'];
    // Check if page should be tracked
    if (savedPageName && !pagesNotToTrack.includes(savedPageName)) {
        tagging.previousPageName = CommonAdobeAnalyticsUtil.constructPageModel(savedPageName, isQuoteAndBuy);
    }
    if (window.location.href.toLowerCase().includes(SG_AA_PAGE_TYPE.IPID)) {
        tagging.previousPageName = CommonAdobeAnalyticsUtil.constructPageModel(
            SgSessionStorageService.getItem(SgSessionStorageService.KEY.LAST_PAGE_NAME)
        );
    }
}

// CTM meal tracking
function populateCtmTracking(url) {
    // CTM click through e.g e.g. https://<existing URL>&ClickthroughId =<UUID>
    const landingPageUrl = url.substring(url.indexOf('ClickthroughId'));
    const clickThroughId = landingPageUrl.replace('ClickthroughId=', '').split('&')[0];
    tagging.clickThroughId = clickThroughId;
    SgSessionStorageService.setItem(SgSessionStorageService.KEY.CLICK_THROUGH_ID, clickThroughId);
}

/**
 * Tracks the current page URL event.
 * @param {Object} event GW Event object
 */
function trackCurrentPageUrl(event) {
    const { location, queryString } = event;
    tagging.currentPageUrl = location;
    const persistentClickThroughId = SgSessionStorageService.getItem(SgSessionStorageService.KEY.CLICK_THROUGH_ID);
    // If compare the market populate CTM tracking
    if (location && location.indexOf('ClickthroughId') > -1) {
        // URL is used to determine tracking as URL/Tracking not populated until
        // after quoteentry & aggentry entry points
        populateCtmTracking(location);
    } else if (!_.isNil(persistentClickThroughId)) {
        tagging.clickThroughId = persistentClickThroughId;
    }
    if (queryString) {
        const persistenScidUrl = CommonAdobeAnalyticsUtil.getScidUrl(queryString)
            || SgSessionStorageService.getItem(SgSessionStorageService.KEY.SCID_VALUE);
        if (persistenScidUrl !== null) {
            tagging.scid = persistenScidUrl;
        }
    }
}

/**
 * Tracks a quoteFieldError event.
 * @param {Object} event GW Event object
 */
function trackQuoteFieldError(event) {
    _.each(event.value, (fieldError) => {
        setHistoryField(event.adobeID, fieldError);
    });
}

/**
 * Sets Adobe tracking data for Communication Preferences in reconsent scenarios(GDPR)
 * @param {Object} preferences preferences changed in session
 * @param {Object} reGdprData preferences retrieved from the service
 */
function trackReGdprData(preferences, reGdprData) {
    const isEmail = reGdprData.channel === '<strong>email</strong>';
    let channelsString = '';
    let divisionsString = '';
    const divisions = reGdprData.division;
    if (preferences.scenariotwo) {
        if ((isEmail && preferences.insuranceEmailSelected) || (!isEmail && preferences.insuranceLetterSelected)) {
            divisionsString += 'insurance|';
        }
        if (!CommonAdobeAnalyticsUtil.wasBusinessAreaPreviouslySelected('<strong>Holidays and Cruises</strong>', divisions)
            && ((isEmail && preferences.travelEmailSelected) || (!isEmail && preferences.travelLetterSelected))) {
            divisionsString += 'holidays|';
        }
        if (!CommonAdobeAnalyticsUtil.wasBusinessAreaPreviouslySelected('<strong>Personal Finance</strong>', divisions)
            && ((isEmail && preferences.financialEmailSelected) || (!isEmail && preferences.financialLetterSelected))) {
            divisionsString += 'personalfinance|';
        }
        if (!CommonAdobeAnalyticsUtil.wasBusinessAreaPreviouslySelected('<strong>Saga Magazine</strong>', divisions)
            && ((isEmail && preferences.magazineEmailSelected) || (!isEmail && preferences.magazineLetterSelected))) {
            divisionsString += 'sagamagazine|';
        }

        if (divisionsString !== '') {
            channelsString = isEmail ? 'email' : 'post';
            divisionsString = divisionsString.slice(0, -1);
        }
    }
    if (preferences.showSingleChannelSingleDivision) {
        if ((isEmail && preferences.insuranceEmailSelected) || (!isEmail && preferences.insuranceLetterSelected)) {
            divisionsString += 'insurance';
        }
        if (divisionsString !== '') {
            channelsString = isEmail ? 'email' : 'post';
        }
    }
    if (preferences.showSingleDivisionMultipleChannels) {
        if (preferences.insuranceEmailSelected || preferences.insuranceLetterSelected) {
            divisionsString += 'insurance';
        }
        if (divisionsString !== '') {
            if (preferences.insuranceEmailSelected) {
                channelsString += 'email|';
            }
            if (preferences.insuranceLetterSelected) {
                channelsString += 'post|';
            }
            channelsString = channelsString.slice(0, -1);
        }
    }
    if (channelsString !== '') {
        tagging.gdprContactType = channelsString;
    }
    if (divisionsString !== '') {
        tagging.gdprBusinessArea = divisionsString;
    }
}

/**
 * Sets Adobe tracking data for Communication Preferences (GDPR)
 * @param {Object} event GW Event object
 */
function trackCommunicationPreferences(event) {
    const { preferences, reGdprData } = event;
    delete tagging.gdprContactType;
    delete tagging.gdprBusinessArea;
    delete tagging.gdprShown;
    delete tagging.gdprReconsent;
    tagging.gdprShown = preferences && (preferences.hasSeenCommunicationPreferences || preferences.isGdprReConsent) ? 'yes' : 'no';
    if (tagging.gdprShown === 'yes') {
        tagging.gdprReconsent = preferences.isGdprReConsent ? 'yes' : 'no';
    }
    if (tagging.gdprShown === 'no' || tagging.gdprReconsent !== 'yes') {
        delete tagging.gdprReconsentVariant;
    }
    if (preferences && !_.isNil(reGdprData.channel)) {
        if (preferences.isGdprReConsent && !preferences.showMultipleChannels && reGdprData && reGdprData.channel !== '') {
            trackReGdprData(preferences, reGdprData);
            return;
        }
        const gdprContactType = CommonAdobeAnalyticsUtil.getContactTypeString(preferences);
        if (gdprContactType !== '') {
            tagging.gdprContactType = gdprContactType;
            const gdprBusinessArea = CommonAdobeAnalyticsUtil.getBusinessAreaString(preferences);
            if (gdprBusinessArea !== '') {
                tagging.gdprBusinessArea = gdprBusinessArea;
            }
        } else if (preferences.isGdprConsentTest) {
            if (preferences.noneSelected) {
                tagging.gdprContactType = 'NoneOfThese';
                tagging.gdprBusinessArea = undefined;
            }
        }
    }
}

/**
 * Sets Adobe tracking data for Customer Permissions(GDPR)
 * @param {Object} event GW Event object
 */
function trackCustomerPermissions(event) {
    const { trackingData, askSagaConsent } = event;
    delete tagging.gdprPrefReturned;
    delete tagging.gdprWordingID;
    delete tagging.gdprBusinessArea;
    delete tagging.gdprConsent;
    delete tagging.gdprShown;
    if (askSagaConsent) {
        tagging.gdprShown = 'Yes';
    } else {
        tagging.gdprShown = 'No';
    }
    if (trackingData) {
        if (trackingData.gdprPrefReturned) {
            tagging.gdprPrefReturned = 'Yes';
        } else {
            tagging.gdprPrefReturned = 'No';
        }
        if (trackingData.wordingID) {
            tagging.gdprWordingID = trackingData.wordingID;
        }
        if (trackingData.optInType) {
            if (trackingData.optInType === 'Custom') {
                tagging.gdprConsent = 'Choose';
                if (trackingData.selectedGroupArea) {
                    tagging.gdprBusinessArea = trackingData.selectedGroupArea;
                }
            } else {
                tagging.gdprConsent = trackingData.optInType;
                tagging.gdprBusinessArea = '';
            }
        }
    }
}

/**
 * Sets Adobe tracking data for error page code.
 * @param {Object} event GW Event object
 */
function trackErrorPageCode(event) {
    const payloadError = event.queryString.replace(/%26/g, '&').replace(/%3D/g, '=').toLowerCase();
    const errorPageCode = payloadError.substring(payloadError.indexOf('error?') + 1, payloadError.indexOf('&md'));
    tagging.errorPageCode = payloadError.indexOf('&md') > -1 ? errorPageCode : payloadError;
}

// eslint-disable-next-line valid-jsdoc
/**
 * Initialise membershipArray.
 * remove activationid if not included
 */
function populateMembershipArray(membershipData) {
    tagging.membership = [];
    // remove activation id from array if ineligible
    if (membershipData.encryptedActivationId === 'null') {
        tagging.membership.push({
            status: membershipData.status
        });
        // If eligible, populate the full array
    } else {
        tagging.membership.push({
            status: membershipData.status,
            id: membershipData.encryptedActivationId
        });
    }
}

/* function trackCpcSaveError() {
    if (tagging.cpcSaveErrorNumber === undefined) {
        tagging.cpcSaveErrorNumber = 1;
    } else {
        tagging.cpcSaveErrorNumber += 1;
    }
} */

function getAnnualPremium(submission) {
    if (SgSubmissionUtil.isIndicativeQuote(submission)) {
        if (_.get(submission, 'baseData.productCode.value') === CommonConstants.HOME_PRODUCT_CODE) {
            return submission.lobData.privateHome.offerings.value[0].coverages.multiplePremiumsData.unselectedOfferTotal.amount.toFixed(2);
        }
        if (_.get(submission, 'baseData.productCode.value') === CommonConstants.MOTOR_PRODUCT_CODE) {
            return SgSubmissionUtil.getAnnualPaymentPlan(submission).total.standardPriceAmount.toFixed(2);
        }
    }
    return SgSubmissionUtil.getAnnualPremium(submission);
}

function getMonthlyInstallment(submission, monthlyPlan) {
    if (SgSubmissionUtil.isIndicativeQuote(submission)) {
        if (_.get(submission, 'baseData.productCode.value') === CommonConstants.HOME_PRODUCT_CODE) {
            return submission.lobData.privateHome.offerings.value[0].coverages.multiplePremiumsData.unselectedOfferTotalMonthly.amount.toFixed(2);
        }
        if (_.get(submission, 'baseData.productCode.value') === CommonConstants.MOTOR_PRODUCT_CODE) {
            return monthlyPlan.installment.standardPriceAmount.toFixed(2);
        }
    }
    return monthlyPlan.installment.amount.toFixed(2);
}
/**
 * Populates premium-related data.
 * @param {Object} submission Submission object
 */
function populateQuoteAdobeTracking(submission) {
    if (submission.bindData && submission.bindData.paymentPlans) {
        // Annual related
        const annualPremium = getAnnualPremium(submission);
        if (annualPremium) {
            tagging.annualPremium = annualPremium;
            setHistoryField(SG_AA_FIELDS.annualPriceCostChange, tagging.annualPremium);
        }
        // Monthly related
        const monthlyPlan = SgSubmissionUtil.getMonthlyPaymentPlan(submission) || SgSubmissionUtil.getMonthlyUpfrontPaymentPlan(submission);
        if (monthlyPlan) {
            tagging.monthlyInstalmentValue = getMonthlyInstallment(submission, monthlyPlan);
            setHistoryField(SG_AA_FIELDS.monthlyPriceCostChange, tagging.monthlyInstalmentValue);
            setAdobeTaggingProp(SG_AA_FIELDS.monthlyPriceDisplayed.id, true);
            tagging.aprValue = monthlyPlan.apr_Ext;
        } else {
            setAdobeTaggingProp(SG_AA_FIELDS.monthlyPriceDisplayed.id, false);
        }
    }
    delete tagging.quoteSection;
}

export default {
    init(combinedTaggingProperties, adobeTagging, isQnB) {
        tagging = adobeTagging;
        SG_AA_FIELDS = combinedTaggingProperties;
        isQuoteAndBuy = isQnB;

        if (!eventsSubscribed) {
            eventsSubscribed = true;
            // Execute cleanup operations on all events
            subscribe([ADOBE_TOPICS.ADOBE_EVENT], (event) => {
                // Do not cleanup
                setAndTrackAdobeField(event);
            });

            subscribe([ADOBE_TOPICS.QUOTE_FIELD_ERROR], (event) => {
                cleanup();
                trackQuoteFieldError({ adobeID: event.adobeID.quoteFieldError, value: event.idArray });
                trackFieldError({ adobeID: event.adobeID.fieldErrorArray, value: event.messagesArray });
            });

            subscribe([JUTRO_TOPICS.ROUTE_CHANGED], (event) => {
                if (isQuoteAndBuy) {
                    cleanup(event);
                    trackPreviousPageName();
                    trackPageName(event);
                }
                trackCurrentPageUrl(event);
            });

            // does not require cleanup
            subscribe([ADOBE_TOPICS.RESET], () => {
                reset();
            });

            subscribe([ADOBE_TOPICS.ADDON_CHANGE], (event) => {
            // Do not cleanup
                trackAddonChange(event);
            });

            subscribe([ADOBE_TOPICS.MEMBERSHIP], (event) => {
                cleanup();
                populateMembershipArray(event.model);
            });

            subscribe([ADOBE_TOPICS.PREMIUM_ACCORDION], (event) => {
                cleanup();
                setHistoryField(event.adobeID, event.value);
            });

            subscribe([ADOBE_TOPICS.ERROR_PAGE], (event) => {
                cleanup();
                trackErrorPageCode(event);
            });

            subscribe([JUTRO_TOPICS.VALUE_CHANGED, JUTRO_TOPICS.FIELD_BLUR], (event) => {
                const { value, id, adobeID } = event;
                const payload = {
                    adobeID: adobeID || id,
                    value,
                    trackLastInteraction: true
                };
                setAndTrackAdobeField(payload);
            });

            subscribe([JUTRO_TOPICS.BUTTON_CLICKED, JUTRO_TOPICS.LINK_CLICKED], (event) => {
                const { id, adobeId } = event;
                let { adobeValue } = event;
                const pageWithParameters = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
                const page = pageWithParameters.indexOf('?') !== -1
                    ? pageWithParameters.substring(0, pageWithParameters.indexOf('?'))
                    : pageWithParameters;
                adobeValue = adobeValue || (event.componentProps && event.componentProps.adobeValue);
                const payload = {
                    adobeID: SG_AA_PREMIUM_PAGE_ACTION_PAGES.includes(page) && adobeValue ? SG_AA_FIELDS.BHpremiumPageAction : SG_AA_FIELDS.ctaText,
                    value: adobeValue || adobeId || id,
                };
                tagging.lastInteraction = payload.value;
                setAndTrackAdobeField(payload);
            });

            subscribe([ADOBE_TOPICS.COMMUNICATION_PREFERENCES], (event) => {
                trackCommunicationPreferences(event);
            });
            subscribe([ADOBE_TOPICS.CUSTOMER_PERMISSIONS], (event) => {
                trackCustomerPermissions(event);
            });
        }
    },
    setHistoryField,
    populateQuoteAdobeTracking,
    cleanup,
    // trackCpcSaveError() {
    //     return trackCpcSaveError.apply(this, arguments);
    // },
};
